import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-final-form";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import Input from "rs-components/Input";
import TextTruncate from "components/TextTruncate";
import SimpleRemoveButton from "components/SimpleRemoveButton";
import { SimulationFormFields } from "../../../..";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { setDeleteTarget } from "store/knowledge-base-item/knowledge-base-item.actions";
import { RemovableEntityType } from "store/knowledge-base-item/knowledge-base-item.types";
import { VariantLabels } from "shared/labels";
import { isNotEmpty } from "utils/validate";
import { getFieldRestrictions } from "store/knowledge-base-item/knowledge-base-item.utils";
import * as styles from "./styles.module.scss";
const AlternativeFlowStep = ({ name, renderProps, index, recipeSteps, formParameters, fieldArrayName, restrictions, incomplete, onVariantConfigurationFieldChange, }) => {
    const { fields } = renderProps;
    const form = useForm();
    const dispatch = useDispatch();
    const editable = useSelector(isEditableSelector);
    const formStepConfig = useMemo(() => fields.value?.[index], [fields.value, index]);
    const selectedRecipeStep = useMemo(() => recipeSteps?.find(({ id }) => id === formStepConfig?.stepId), [recipeSteps, formStepConfig?.stepId]);
    const flowsOptions = useMemo(() => selectedRecipeStep?.flowProperties
        .filter(({ isDefault }) => !isDefault)
        .map(({ id }, index) => ({ value: id, label: `Alternative flow ${index + 1}` })), [selectedRecipeStep?.flowProperties]);
    const setAlternativeFlowDeleteTarget = useCallback(() => {
        dispatch(setDeleteTarget({
            id: index,
            type: RemovableEntityType.SIMULATION_VARIANT_FLOW,
        }));
    }, [dispatch, index]);
    const onFlowChange = useCallback((flowId) => {
        const unChangedParameters = formParameters?.filter(({ stepId, flowId }) => stepId !== formStepConfig?.stepId || flowId !== formStepConfig?.flowId);
        const updatedVariantFields = {
            [SimulationFormFields.flowConfig]: {
                [SimulationFormFields.stepsConfig]: [
                    ...unChangedParameters,
                    { stepId: formStepConfig?.stepId, flowId },
                ],
            },
        };
        onVariantConfigurationFieldChange(updatedVariantFields);
    }, [formStepConfig, formParameters, onVariantConfigurationFieldChange]);
    const onRemoveParameter = useCallback((isConfirmed) => {
        if (!isConfirmed) {
            return;
        }
        const updatedParameters = formParameters.filter((_, i) => i !== index);
        const updatedVariantFields = {
            [SimulationFormFields.flowConfig]: { [SimulationFormFields.stepsConfig]: updatedParameters },
        };
        onVariantConfigurationFieldChange(updatedVariantFields);
        form.change(fieldArrayName, updatedParameters);
    }, [form, fieldArrayName, formParameters, index, onVariantConfigurationFieldChange]);
    return (React.createElement("div", { key: `${name}-${index}-${fields?.length}`, className: styles.parameter },
        React.createElement(TextTruncate, { className: styles.title, text: selectedRecipeStep?.name }),
        editable && (React.createElement(SimpleRemoveButton, { className: styles["parameter-deleteIcon"], onOpen: setAlternativeFlowDeleteTarget, action: onRemoveParameter, isMenuItem: false, withConfirmation: isNotEmpty(formStepConfig?.flowId) })),
        React.createElement(Input, { name: `${name}.${SimulationFormFields.flowId}`, component: SelectInput, items: flowsOptions, itemRenderer: SelectItemRenderer, valueFormatter: ({ value }) => value, placeholder: "Select", classes: { component: styles.select, label: styles.label }, readonly: !editable, errorLabel: "Parameter", restrictions: getFieldRestrictions(restrictions, ["FlowId"]), onChange: onFlowChange }),
        !flowsOptions?.length && (React.createElement("span", { className: styles.noOptions }, VariantLabels.NO_ALTERNATIVE_FLOWS_IN_STEP)),
        incomplete && (React.createElement(TextTruncate, { className: styles.errorMessage, text: VariantLabels.ALTERNATIVE_FLOW_NOT_VALID }))));
};
export default AlternativeFlowStep;
